import React from 'react';
import { useConnections } from '@/hooks/useConnections';
import { Users, Calendar, ShieldCheck, ExternalLink, AlertCircle, PlusCircle, Stethoscope, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DoctorScheduleModal } from './DoctorScheduleModal';
import { useAuth } from '@/hooks/useAuth';
import { formatDoctorSpecialty } from '@/lib/doctorSpecialty';

interface ConnectionsListProps {
  role: 'PATIENT' | 'DOCTOR';
}

export const ConnectionsList: React.FC<ConnectionsListProps> = ({ role }) => {
  const { connections, isLoading, error, refresh } = useConnections(role);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scheduleModalData, setScheduleModalData] = React.useState<{id: string, name: string} | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl text-center">
        <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-400 mb-3">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          Ops! Tivemos um problema
        </h3>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 max-w-xs">
          {error}. Tente novamente em instantes.
        </p>
        <button
          type="button"
          onClick={() => refresh()}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60"
        >
          <RefreshCw className="h-4 w-4" />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="text-center p-10 bg-slate-50/50 dark:bg-neutral-900/50 rounded-2xl border-2 border-dashed border-slate-200 dark:border-neutral-800 transition-all">
        <div className="mx-auto h-16 w-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
          <Users className="h-8 w-8 text-blue-400 dark:text-blue-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {role === 'DOCTOR' ? 'Sua rede de pacientes' : 'Seus Médicos'}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
          {role === 'DOCTOR' 
            ? 'Atenda pacientes no plantão ou por agendamento para estabelecer vínculos permanentes e acompanhar o histórico de saúde.' 
            : 'Você ainda não possui médicos na sua rede. Que tal agendar sua primeira consulta?'}
        </p>
        
        {role === 'PATIENT' && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button 
              onClick={() => navigate('/buscar-medicos')}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-teal-500/20 transition-all hover:-translate-y-0.5"
            >
              Agendar Primeira Consulta
            </button>
            <button 
              onClick={() => navigate('/on-demand/triage')}
              className="px-6 py-2.5 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-neutral-700 transition-all"
            >
              Falar com Plantonista
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {connections.map((link) => {
        const connectedUser = role === 'DOCTOR' ? link.patient : link.doctor;
        const displayName = connectedUser?.name || (role === 'PATIENT' ? 'Médico' : 'Paciente');
        const doctorSpecialty =
          role === 'PATIENT' ? formatDoctorSpecialty(link.doctor?.specialty) : null;
        const bookingDoctorId =
          role === 'PATIENT'
            ? link.doctor?.profileId || link.doctor?.id || link.doctorId
            : undefined;

        return (
          <div 
            key={link.id}
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4 gap-2">
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <div className="shrink-0 h-10 w-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white truncate" title={displayName}>
                    {displayName}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={connectedUser?.email || ''}>
                    {connectedUser?.email || '—'}
                  </p>
                  {doctorSpecialty && (
                    <p
                      className="text-xs text-teal-700 dark:text-teal-400 mt-0.5 flex items-center gap-1"
                      title={doctorSpecialty}
                    >
                      <Stethoscope className="shrink-0 h-3 w-3" />
                      <span className="truncate">{doctorSpecialty}</span>
                    </p>
                  )}
                </div>
              </div>
              <span className={`shrink-0 text-[10px] px-2 py-1 rounded-full font-medium ${
                link.source === 'on-demand' 
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' 
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              }`}>
                {link.source === 'on-demand' ? 'PLANTÃO' : 'AGENDADO'}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <Calendar className="h-3.5 w-3.5 mr-2" />
                <span>Primeiro contato: {new Date(link.firstEncounterDate).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                <ShieldCheck className="h-3.5 w-3.5 mr-2" />
                <span>Último atendimento: {new Date(link.lastEncounterDate).toLocaleDateString()}</span>
              </div>
            </div>

            {role === 'DOCTOR' ? (
              <div className="flex gap-2 w-full mt-2">
                <button 
                  onClick={() => navigate(`/prontuario/${connectedUser?.id}`)}
                  className="flex-1 py-2 px-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-teal-50 dark:hover:bg-teal-900/20 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center group"
                >
                  Prontuário
                  <ExternalLink className="ml-1.5 h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <button 
                  onClick={() => setScheduleModalData({ id: connectedUser?.id || '', name: connectedUser?.name || '' })}
                  className="flex-1 py-2 px-4 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/40 text-teal-700 dark:text-teal-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center group"
                >
                  <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Retorno
                </button>
              </div>
            ) : (
              <button 
                onClick={() => navigate(`/agendamento/${bookingDoctorId}`)}
                disabled={!bookingDoctorId}
                className="w-full mt-2 py-2 px-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-teal-50 dark:hover:bg-teal-900/20 text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center group disabled:opacity-50"
              >
                Nova Consulta
                <ExternalLink className="ml-2 h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
        );
      })}

      <DoctorScheduleModal
        isOpen={!!scheduleModalData}
        onClose={() => setScheduleModalData(null)}
        patientId={scheduleModalData?.id || ''}
        patientName={scheduleModalData?.name || ''}
        doctorId={user?.id || ''}
      />
    </div>
  );
};
